import React from "react";
import axios from "axios";

import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { persistor, store } from "./utils/redux/store";
import { Provider } from "react-redux";
import { LoadingModal } from "./utils/context/loading";
import { ChakraProvider, ColorModeScript } from "@chakra-ui/react";
import { adminTheme } from "./theme";
import "./admin-theme.css";

// Send the session cookie with every API call — but only once the SERVER is
// ready for it.
//
// This is off by default, and that default matters. A browser REFUSES a
// credentialed request whose response carries `Access-Control-Allow-Origin: *`,
// Auth is an httpOnly cookie, not a token this code can read.
//
// It got here in two deliberate steps, and the order mattered:
//   1. The API set the cookie AND still returned the token, behind a flag here,
//      so a mistake in the cookie attributes could not lock every admin out.
//      Enabling credentials before the API's ADMIN_ORIGINS was configured DID
//      happen once: every request failed before it had a response body and the
//      panel showed "Error: undefined" with nobody able to log in.
//   2. Once a real login was confirmed over the cookie, the API stopped
//      returning the token and this stopped storing one.
//
// The prerequisite is permanent, not transitional: the API must send
// `Access-Control-Allow-Origin: https://admin.pureworker.com` (not `*`) and
// `Access-Control-Allow-Credentials: true`, which needs ADMIN_ORIGINS set. If
// the panel ever moves to a domain outside pureworker.com, the cookie stops
// being first-party and this breaks again.
axios.defaults.withCredentials = true;

// STEP 2, the one that actually closes the hole. The API no longer returns the
// token in the login body, so there is nothing to put in localStorage and
// nothing for a script on this page to steal. The httpOnly cookie carries auth.
//
// The panel has ~60 call sites that build `Authorization: Bearer ${token}` from
// localStorage. With no token they would send `Bearer null`, and the API's
// deserializeUser refuses an empty bearer BEFORE it ever looks at the cookie —
// so every request would 401 despite a perfectly good cookie being present.
//
// Rather than rewrite sixty call sites (a large mechanical diff across the
// busiest file in the panel, for no behavioural gain), one interceptor drops an
// Authorization header that carries no real token. A caller that genuinely has
// one — nothing in this panel today, but the shape is preserved — still sends it.
axios.interceptors.request.use((config) => {
  const auth = config.headers?.Authorization;
  if (typeof auth === "string" && /^Bearer\s*(null|undefined)?$/.test(auth.trim())) {
    delete config.headers.Authorization;
  }
  return config;
});

// This panel logs a lot (API responses, provider and customer records, and
// until recently the admin's own JWT on every call). An admin's browser console
// is read by screen shares, recordings, extensions, and anyone walking past a
// logged-in machine, so production says nothing. Errors and warnings stay: they
// are what you need when something actually breaks.
//
// This silences output rather than stripping the calls, because plain
// create-react-app gives no hook for babel-plugin-transform-remove-console
// without adding craco or ejecting. If this repo ever gains craco, swap to that
// plugin so the strings leave the bundle too.
if (process.env.NODE_ENV === "production") {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <React.StrictMode>
    <ColorModeScript initialColorMode="light" />
    <BrowserRouter>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ChakraProvider theme={adminTheme}>
            <App />
          </ChakraProvider>
        </PersistGate>
      </Provider>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
